// src/hooks/useGiftCodeSearch.ts
/*
//ギフトコードの管理画面でギフトコードをフィルタリングするサービス
*/
import { useState, useEffect } from 'react';
import { GiftCode } from '../types';

/**
 * ギフトコードの検索とフィルタリングを管理するカスタムフック
 * ギフトコード一覧を元に、フィルタリングした結果を返します。
 *
 * @param allGiftCodes - フィルタリング対象の全ギフトコードの配列
 * @returns
 * - filteredGiftCodes: フィルタリングされたギフトコードの配列
 * - searchActive: アクティブ状態の検索フィルター
 * - setSearchActive: アクティブ状態フィルターを設定する関数
 * - searchGiftCode: ギフトコード文字列の検索フィルター
 * - setSearchGiftCode: ギフトコード文字列フィルターを設定する関数
 * - searchMaxDlCountExists: 最大DL数存在の検索フィルター
 * - setSearchMaxDlCountExists: 最大DL数存在フィルターを設定する関数
 * - searchAllowedUsersExists: 許可ユーザー存在の検索フィルター
 * - setSearchAllowedUsersExists: 許可ユーザー存在フィルターを設定する関数
 */
export const useGiftCodeSearch = (allGiftCodes: GiftCode[]) => {
    const [filteredGiftCodes, setFilteredGiftCodes] = useState<GiftCode[]>([]);

    // 検索フィルターの状態
    const [searchActive, setSearchActive] = useState<string>('all'); // 'all', 'true', 'false'
    const [searchGiftCode, setSearchGiftCode] = useState<string>('');
    const [searchMaxDlCountExists, setSearchMaxDlCountExists] = useState<string>('all'); // 'all', 'true', 'false'
    const [searchAllowedUsersExists, setSearchAllowedUsersExists] = useState<string>('all'); // 'all', 'true', 'false'

    // フィルターの適用ロジック
    useEffect(() => {
        let currentFiltered = allGiftCodes;

        // active/passive フィルター
        if (searchActive !== 'all') {
            const isActive = searchActive === 'true';
            currentFiltered = currentFiltered.filter(gc => gc.active === isActive);
        }

        // giftCode フィルター
        if (searchGiftCode.trim() !== '') {
            currentFiltered = currentFiltered.filter(gc =>
                gc.giftCode.toLowerCase().includes(searchGiftCode.trim().toLowerCase())
            );
        }

        // max_dl_count フィルター
        if (searchMaxDlCountExists !== 'all') {
            const exists = searchMaxDlCountExists === 'true';
            currentFiltered = currentFiltered.filter(gc =>
                exists ? gc.max_dl_count !== undefined : gc.max_dl_count === undefined
            );
        }

        // allowed_users フィルター
        if (searchAllowedUsersExists !== 'all') {
            const exists = searchAllowedUsersExists === 'true';
            currentFiltered = currentFiltered.filter(gc =>
                exists ? (gc.allowed_users && gc.allowed_users.length > 0) : (!gc.allowed_users || gc.allowed_users.length === 0)
            );
        }

        setFilteredGiftCodes(currentFiltered);
    }, [allGiftCodes, searchActive, searchGiftCode, searchMaxDlCountExists, searchAllowedUsersExists]);

    return {
        filteredGiftCodes,
        searchActive,
        setSearchActive,
        searchGiftCode,
        setSearchGiftCode,
        searchMaxDlCountExists,
        setSearchMaxDlCountExists,
        searchAllowedUsersExists,
        setSearchAllowedUsersExists
    };
};
